import FormHelperText from "@material-ui/core/FormHelperText";
import Joi from "joi";
import _ from "lodash";
import React from "react";
import NumberFormat from "react-number-format";
import {toast} from "react-toastify";
import EAButton from "../components/EAButton";
import EACheckbox from "../components/EACheckbox";
import EACombobox from "../components/EACombobox";
import EADatePicker from "../components/EADatePicker";
import EASelect from "../components/EASelect";
import EATextField from "../components/EATextField";
import {isApiCancel, useHttp} from "../services/HttpService";
import {logger} from "../services/LogService";
import EAComboboxWithCustomLabelFunction from "../components/EAComboboxWithCustomLabelFunction";

function personalisiertValueExtractor(isPersonalisiert, values, id, multiple) {
    const personalisiertIndicator = {
        id: null,
        value: "personalisiert"
    };

    return isPersonalisiert ? personalisiertIndicator : (_.get(values, id) ? _.get(values, id) : (multiple ? [] : null));
}

function NumberFormatCustom(props) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            allowLeadingZeros={false}
            thousandSeparator="."
            decimalSeparator=","
            isNumericString
        />
    );
}

function NumberFormatNoThousandSeparator(props) {
    const {
        inputRef,
        onChange,
        ...other
    } = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            allowLeadingZeros={false}
            thousandSeparator=""
            decimalSeparator=","
            isNumericString
        />
    );
}

function NumberFormatNoDecimalPlaces(props) {
    const {
        inputRef,
        onChange,
        ...other
    } = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            allowLeadingZeros={false}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={0}
            fixedDecimalScale={true}
            isNumericString
        />
    );
}

function NumberFormatOnlyPositiveIntegers(props) {
    const {
        inputRef,
        onChange,
        ...other
    } = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            allowLeadingZeros={false}
            thousandSeparator="."
            decimalSeparator=","
            decimalScale={0}
            fixedDecimalScale={true}
            allowNegative={false}
            isNumericString
        />
    );
}

export function useForm({
                            initialValues = null,
                            schema: initialSchema = null,
                            readonly = false
                        }) {
    const [values, setValues] = React.useState(initialValues || {});
    const [touchedValues, setTouchedValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [schema, setSchema] = React.useState(initialSchema);
    const [{loading: submitLoading}, doSubmit] = useHttp({}, {manual: true});

    /**
     * Funktion, welche zuerst Validiert, dann die Funktion zum vorbereiten der values mit einer Kopie der values aufruft und abschließend
     * das Backend mit der entsprechenden URL, der Methode und den vorbereiteten Daten anspricht. Sollten Validierungsfehler zurückkommen,
     * wird die Exception weitergeworfen und hat dann das zusätzliche Feld "isValidationHandled"
     * @param prepareForSubmit Funktion, welche eine Kopie der values erhält, um die Daten für das Backend vorzubereiten.
     * @param url
     * @param method
     * @param handleValidationInternally gibt an ob Fehler intern behandelt oder zum Aufrufer weitergereicht werden sollen
     * @param dataToAppend zusaetzliche Daten die an den Request angehaengt werden sollen
     * @returns {Promise<{then: then}>|Promise<{then: then, catch: catch, finally: finally} | any>}
     */
    function submit(prepareForSubmit, url, method, handleValidationInternally = true, dataToAppend = null) {
        if (method !== "DELETE") {
            const newErrors = validate();
            if (newErrors) {
                return Promise.resolve({
                    then: function () {
                    },
                });
            }
        }
        let data = prepareForSubmit(_.cloneDeep(values));
        if (dataToAppend) {
            data = {
                ...data,
                auditInfo: dataToAppend,
            };
        }
        logger.debug("Daten werden ans Backend gesendet", data);
        return doSubmit({
            url,
            method,
            data,
        }).catch((ex) => {
            if (isApiCancel(ex)) {
                return Promise.resolve({
                    then: function () {
                    },
                });
            }
            if (ex.response?.data?.validationErrors && handleValidationInternally) {
                setErrors(ex.response.data.validationErrors || {});

                const errorMessage = Object.values(ex.response.data.validationErrors)[0];
                toast.warn("Validierung fehlgeschlagen: " + errorMessage);

                ex.isValidationHandled = true;
            }
            throw ex;
        });
    }

    function validate() {
        if (!schema) {
            return null;
        }
        const useToValidate = Joi.isSchema(schema) ? schema : Joi.object(schema);
        const {error} = useToValidate.validate(
            {...values},
            {
                abortEarly: false,
                stripUnknown: true,
            }
        );
        if (!error) {
            return null;
        }
        const newErrors = {};
        for (let err of error.details) {
            logger.debug(err);
            newErrors[_.join(err.path, ".")] = err.message;
        }
        setErrors({
            ...newErrors,
        });
        return newErrors;
    }

    function validateDirectly(object) {
        if (!schema) {
            return null;
        }
        const useToValidate = Joi.isSchema(schema) ? schema : Joi.object(schema);
        const {error} = useToValidate.validate(object, {
            abortEarly: false,
            stripUnknown: true,
        });

        if (!error) {
            return null;
        }
        const newErrors = {};
        for (let err of error.details) {
            logger.debug(err);
            newErrors[_.join(err.path, ".")] = err.message;
        }

        return newErrors;
    }

    function validateProperty(name, value) {
        if (!schema) {
            return null;
        }
        if (_.get(schema, name) === undefined) {
            return null;
        }
        const naming = _.last(_.split(name, "."));
        const obj = {[naming]: value};
        const spezificSchema = {
            [naming]: _.get(schema, name),
        };

        logger.debug(spezificSchema);

        const useToValidate = Joi.isSchema(spezificSchema) ? spezificSchema : Joi.object(spezificSchema);
        const {error} = useToValidate.validate(obj, {
            stripUnknown: true,
        });
        if (error) {
            logger.debug(error.details);
        }
        return error ? error.details[0].message : null;
    }

    const changeWithValidation = (name, value, dependentValues) => {
        const stateToSet = {
            valuesToSet: {...values},
            touchedValuesToSet: {...touchedValues},
            errorsToSet: {...errors},
        };
        calcChangedState(stateToSet, name, value);
        if (Array.isArray(dependentValues)) {
            dependentValues.forEach((dV) => calcChangedState(stateToSet, dV.id, dV.calcDependentVal(value, stateToSet.valuesToSet)));
        }
        setValues(stateToSet.valuesToSet);
        setTouchedValues(stateToSet.touchedValuesToSet);
        setErrors(stateToSet.errorsToSet);
    };

    const calcChangedState = (stateToSet, name, value) => {
        _.set(stateToSet.valuesToSet, name, value);
        _.set(stateToSet.touchedValuesToSet, name, true);
        const e = validateProperty(name, value);
        if (e) {
            _.set(stateToSet.errorsToSet, name, e);
        } else {
            _.unset(stateToSet.errorsToSet, name);
            if (
                name.indexOf(".") !== -1 &&
                _.get(stateToSet.errorsToSet, name.substring(0, name.indexOf("."))) &&
                Object.keys(_.get(stateToSet.errorsToSet, name.substring(0, name.indexOf(".")))).length === 0
            ) {
                _.unset(stateToSet.errorsToSet, name.substring(0, name.indexOf(".")));
            }
        }
    };

    const handleChange = (event, dependentValues) => {
        const target = event.target;
        const value = target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;
        changeWithValidation(name, value, dependentValues);
    };

    const formFactory = {
        getReadonly() {
            if (typeof readonly === "function") {
                return readonly();
            }
            return readonly;
        },
        renderButton: (id, testId, label, onClick, rest) => {
            if (formFactory.getReadonly()) {
                return <span/>;
            }
            return <EAButton id={id} data-cy={testId} key={id} label={label} onClick={onClick} {...rest} />;
        },

        /**
         * Rendert eine Fehlermeldung, falls diese vorhanden sein sollte.
         * @param id der Pfad zum Objekt
         * @param testId id, welche für tests benutzt werden sollte
         * @param pre Ein vor der Errormeldung darzustellender Text
         * @param post Ein nach der Errormeldung darzustellender Text
         * @param rest
         * @returns {JSX.Element|null}
         */
        renderError: (id, testId, pre = null, post = null, rest = null) => {
            const error = _.get(errors, id);
            if (!error) {
                return null;
            }
            return (
                <FormHelperText id={id} data-cy={testId} error {...rest}>
                    {(
                        pre ? pre : "") + error + (
                        post ? post : "")}
                </FormHelperText>
            );
        },

        renderInput: (id, testId, label, type = "text", disabled = false, rest = null, isValid = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    type={type}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(event) => {
                        if (!isValid || isValid(event)) {
                            handleChange(event);
                        }
                    }}
                    {...rest}
                />
            );
        },

        renderInputWithCustomDisplay: (id, testId, label, customDisplayFunction, disabled = false, rest = null, type = "text", isValid = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    type={type}
                    disabled={disabled || formFactory.getReadonly()}
                    value={customDisplayFunction(_.get(values, id) ? _.get(values, id) : "")}
                    error={_.get(errors, id)}
                    onChange={(event) => {
                        if (!isValid || isValid(event)) {
                            handleChange(event);
                        }
                    }}
                    {...rest}
                />
            );
        },

        renderNumberInput: (id, testId, label, disabled = false, rest = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(event) => handleChange(event)}
                    InputProps={{
                        inputComponent: NumberFormatCustom,
                    }}
                    {...rest}
                />
            );
        },
        renderNumberInputNoThousandSeparator: (id, testId, label, disabled = false, rest = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(event) => handleChange(event)}
                    InputProps={{
                        inputComponent: NumberFormatNoThousandSeparator,
                    }}
                    {...rest}
                />
            );
        },
        renderNumberInputNoDecimalPlaces: (id, testId, label, disabled = false, rest = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(event) => handleChange(event)}
                    InputProps={{
                        inputComponent: NumberFormatNoDecimalPlaces,
                    }}
                    {...rest}
                />
            );
        },
        renderNumberInputOnlyPositiveIntegers: (id, testId, label, disabled = false, rest = null) => {
            return (
                <EATextField
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(event) => handleChange(event)}
                    InputProps={{
                        inputComponent: NumberFormatOnlyPositiveIntegers,
                    }}
                    {...rest}
                />
            );
        },
        renderDatePicker: (id, testId, label, disabled = false, rest = null) => {
            return (
                <EADatePicker
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={disabled || formFactory.getReadonly()}
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(dateString) => {
                        changeWithValidation(id, dateString);
                    }}
                    {...rest}
                />
            );
        },
        renderDatePickerPersonalisiert: (id, testId, label, disabled = false, isPersonalisiert = false, rest = null) => {
            return (
                <EADatePicker
                    id={id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    label={label}
                    disabled={isPersonalisiert || disabled || formFactory.getReadonly()}
                    value={isPersonalisiert ? null : (_.get(values, id) ? _.get(values, id) : "")}
                    error={_.get(errors, id)}
                    onChange={(dateString) => {
                        changeWithValidation(id, dateString);
                    }}
                    {...rest}
                />
            );
        },

        renderSelect: (id, testId, label, options, disabled = false, rest = null, dependentValues = null) => {
            return (
                <EASelect
                    name={id}
                    data-cy={testId}
                    key={id}
                    label={label}
                    options={options ? options : []}
                    disabled={disabled || formFactory.getReadonly()}
                    optionTitle="value"
                    optionKey="id"
                    value={_.get(values, id) ? _.get(values, id) : ""}
                    error={_.get(errors, id)}
                    onChange={(newValue) => {
                        changeWithValidation(id, newValue, dependentValues);
                    }}
                    {...rest}
                />
            );
        },

        renderCheckbox: (id, testId, label, disabled = false, rest = null, dependentValues = null) => {
            return (
                <EACheckbox
                    id={rest && rest.forId ? rest.forId : id}
                    data-cy={testId}
                    key={id}
                    name={id}
                    disabled={disabled || formFactory.getReadonly()}
                    checked={_.get(values, id) ? _.get(values, id) : false}
                    error={_.get(errors, id)}
                    onChange={(event) => handleChange(event, dependentValues)}
                    children={label}
                    {...rest}
                />
            );
        },

        renderAutocompletePersonalisiert: (
            id,
            testId,
            label,
            options,
            optionTitle = null,
            optionKey = null,
            loading = false,
            multiple = false,
            disabled = false,
            rest = null,
            dependentValues = null,
            isPersonalisiert = false,
            onChange
        ) => {

            // Für personalisierte Suchen ist diese Prüfung auszusetzen, da sonst Fehlermeldungen
            // in der Browser-Konsole ausgegeben werden, da das Default-Personalisierungsobjekt
            // typischerweise keine gültige Auswahl ist
            rest = {
                getOptionSelected: (_option) => true,
                ...rest
            };

            return (
                <EACombobox
                    id={id}
                    data-cy={testId}
                    key={id}
                    label={label}
                    options={options}
                    optionTitle={optionTitle}
                    optionKey={optionKey}
                    disabled={isPersonalisiert || disabled || formFactory.getReadonly()}
                    error={_.get(errors, id)}
                    value={personalisiertValueExtractor(isPersonalisiert, values, id, multiple)}
                    multiple={multiple}
                    disableCloseOnSelect={multiple}
                    loading={loading}
                    onChange={(_event, newValue) => {
                        changeWithValidation(id, newValue, dependentValues);
                        if (onChange) {
                            onChange(newValue);
                        }
                    }}
                    {...rest}
                />
            );
        },

        renderAutocomplete: (
            id,
            testId,
            label,
            options,
            optionTitle = null,
            optionKey = null,
            loading = false,
            multiple = false,
            disabled = false,
            rest = null,
            dependentValues = null
        ) => {
            return (
                <EACombobox
                    id={id}
                    data-cy={testId}
                    key={id}
                    label={label}
                    options={options}
                    optionTitle={optionTitle}
                    optionKey={optionKey}
                    disabled={disabled || formFactory.getReadonly()}
                    error={_.get(errors, id)}
                    value={_.get(values, id) ? _.get(values, id) : (multiple ? [] : null)}
                    multiple={multiple}
                    disableCloseOnSelect={multiple}
                    loading={loading}
                    onChange={(_event, newValue) => {
                        changeWithValidation(id, newValue, dependentValues);
                    }}
                    {...rest}
                />
            );
        },

        renderAutocompleteWithCustomChipLabelFunction: (
            id,
            testId,
            label,
            options,
            chipLabelFunction,
            optionTitle = null,
            optionKey = null,
            loading = false,
            multiple = false,
            disabled = false,
            rest = null,
            dependentValues = null
        ) => {
            return (
                <EAComboboxWithCustomLabelFunction
                    id={id}
                    data-cy={testId}
                    key={id}
                    label={label}
                    options={options}
                    optionTitle={optionTitle}
                    optionKey={optionKey}
                    disabled={disabled || formFactory.getReadonly()}
                    error={_.get(errors, id)}
                    value={_.get(values, id) ? _.get(values, id) : []}
                    multiple={multiple}
                    disableCloseOnSelect={multiple}
                    loading={loading}
                    onChange={(_event, newValue) => {
                        changeWithValidation(id, newValue, dependentValues);
                    }}
                    chipLabelFunction={chipLabelFunction}
                    {...rest}
                />
            );
        },

        /**
         * Erwartet für die Options das von useHttp zurückgegebene Objekt mit den Keys: data und loading.
         * Die Daten der Optionen sollten dann zweigeteilt aufgebaut sein mit den Keys: value und id.
         * @param id
         * @param testId wird als data-cy an das element uebergeben
         * @param label
         * @param options
         * @param multiple
         * @param disabled
         * @param rest Zusätzliche Parameter
         * @param dependentValues vom Feld abhängige Felder mit deren ID und einer Berechnungsfunktion für den neuen Wert
         * @returns {*}
         */
        renderAutocompleteWerteliste: (
            id,
            testId,
            label,
            options,
            multiple = false,
            disabled = false,
            rest = null,
            dependentValues = null,
            shouldSort = true
        ) => {
            return formFactory.renderAutocomplete(
                id,
                testId,
                label,
                options.data,
                "value",
                "id",
                options.loading,
                multiple,
                disabled,
                {
                    getOptionLabel: (option) => (option && option.value) ? option.value : "",
                    shouldSort,
                    ...rest,
                },
                dependentValues
            );
        },
    };

    return {
        values,
        setValues,
        touchedValues,
        setTouchedValues,
        errors,
        setErrors,
        handleChange,
        validate,
        validateDirectly,
        validateProperty,
        formFactory,
        setSchema,
        submit,
        submitLoading,
    };
}
